import type { CommandErrorCode } from "../../lib/command-contract";

/**
 * Contrato local da assistência de mensagens por IA (Plano 03, Lote 5).
 *
 * Nada aqui abre rede, credencial, SDK ou provider real. O domínio descreve
 * prompts versionados, pedidos, resultados imutáveis, feedback humano,
 * catálogos versionados, orçamento puro e handoff. A execução usa uma porta de
 * provider; nesta etapa só existe o fake determinístico.
 */

/**
 * Slugs reais da escada Groq medida no Plano 20. O prefixo `openai/` faz
 * parte do slug hospedado pela Groq; não indica uso da API da OpenAI.
 */
export const AI_MODELS = [
  "openai/gpt-oss-20b",
  "openai/gpt-oss-safeguard-20b",
  "openai/gpt-oss-120b",
] as const;
export type AiModel = (typeof AI_MODELS)[number];

export const AI_CLASSIFICATION_MODEL = "openai/gpt-oss-20b" as const satisfies AiModel;
export const AI_COMPOSITION_MODEL = "openai/gpt-oss-120b" as const satisfies AiModel;

/** Modelo padrão para prompts de composição e compatibilidade com fixtures. */
export const AI_MODEL = AI_COMPOSITION_MODEL;

export const AI_MODEL_LADDERS = {
  classify: [
    AI_CLASSIFICATION_MODEL,
    "openai/gpt-oss-safeguard-20b",
    AI_COMPOSITION_MODEL,
  ],
  compose_reply: [
    AI_COMPOSITION_MODEL,
    "openai/gpt-oss-safeguard-20b",
  ],
} as const satisfies Record<AiObjective, readonly AiModel[]>;

export function isAiModel(value: string): value is AiModel {
  return (AI_MODELS as readonly string[]).includes(value);
}

export function aiModelLadderFor(objective: AiObjective): readonly AiModel[] {
  return AI_MODEL_LADDERS[objective];
}

export const AI_REASONING_EFFORTS = ["low", "medium", "high"] as const;
export type AiReasoningEffort = (typeof AI_REASONING_EFFORTS)[number];

/** Versão do schema estrito de saída. Mudança incompatível exige nova versão. */
export const AI_OUTPUT_SCHEMA_VERSION = 1;

export const AI_OBJECTIVES = ["classify", "compose_reply"] as const;
export type AiObjective = (typeof AI_OBJECTIVES)[number];

export const aiObjectiveLabels: Record<AiObjective, string> = {
  classify: "Classificar a mensagem",
  compose_reply: "Classificar e sugerir resposta",
};

/**
 * `simulated` é o único modo executável neste lote. `shadow` e `live` existem
 * como contrato testado e permanecem desabilitados: nenhum deles possui
 * adapter, worker ou autorização.
 */
export const AI_EXECUTION_MODES = ["simulated", "shadow", "live"] as const;
export type AiExecutionMode = (typeof AI_EXECUTION_MODES)[number];

export const aiExecutionModeLabels: Record<AiExecutionMode, string> = {
  simulated: "Simulado",
  shadow: "Shadow mode",
  live: "Produção",
};

/** Shadow mode é contrato, não capacidade ativa. */
export const AI_SHADOW_MODE_ENABLED = false;
/** Auto-send não existe no Lote 5 e não pode ser ligado por configuração local. */
export const AI_AUTO_SEND_ENABLED = false;

// ---------------------------------------------------------------------------
// Catálogos versionados
// ---------------------------------------------------------------------------

export const REPLY_CLASSIFICATIONS = [
  "interesse",
  "pergunta",
  "objecao",
  "reuniao",
  "nao_interessado",
  "unsubscribe",
  "complaint",
  "out_of_office",
  "bounce",
  "desconhecido",
] as const;
export type ReplyClassification = (typeof REPLY_CLASSIFICATIONS)[number];

export const replyClassificationLabels: Record<ReplyClassification, string> = {
  interesse: "Interesse",
  pergunta: "Pergunta",
  objecao: "Objeção",
  reuniao: "Reunião",
  nao_interessado: "Não interessado",
  unsubscribe: "Descadastro",
  complaint: "Denúncia de spam",
  out_of_office: "Ausência automática",
  bounce: "Retorno de entrega",
  desconhecido: "Desconhecido",
};

/**
 * Classificações que nenhuma sugestão de IA pode neutralizar. Elas vêm de
 * política determinística do canal (Plano 02) e prevalecem sobre o modelo.
 */
export const CRITICAL_CLASSIFICATIONS: readonly ReplyClassification[] = [
  "unsubscribe",
  "complaint",
  "bounce",
];

/** Classificações que nunca recebem resposta automática. */
export const NO_AUTO_REPLY_CLASSIFICATIONS: readonly ReplyClassification[] = [
  "unsubscribe",
  "complaint",
  "bounce",
  "out_of_office",
];

export const APPROACH_AUTOMATION_POLICIES = ["manual_only", "review_required", "automatable"] as const;
export type ApproachAutomationPolicy = (typeof APPROACH_AUTOMATION_POLICIES)[number];

export interface ApproachType {
  id: string;
  name: string;
  description: string;
  positiveExamples: string[];
  negativeExamples: string[];
  automationPolicy: ApproachAutomationPolicy;
  active: boolean;
}

export const CATALOG_STATUSES = ["draft", "published"] as const;
export type CatalogStatus = (typeof CATALOG_STATUSES)[number];

export interface ReplyClassificationVersion {
  id: string;
  ownerId: string;
  version: number;
  status: CatalogStatus;
  classifications: ReplyClassification[];
  publishedAt?: string;
  createdAt: string;
}

export interface ApproachTypeVersion {
  id: string;
  ownerId: string;
  version: number;
  status: CatalogStatus;
  approaches: ApproachType[];
  publishedAt?: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Prompt versionado
// ---------------------------------------------------------------------------

export interface AiPromptVersion {
  id: string;
  ownerId: string;
  objective: AiObjective;
  name: string;
  version: number;
  status: CatalogStatus;
  /** Objetivo declarado da versão, exibido na revisão humana. */
  goal: string;
  /** Instruções confiáveis. Conteúdo citado nunca entra aqui. */
  instructions: string;
  outputSchemaVersion: number;
  model: AiModel;
  reasoningEffort: AiReasoningEffort;
  maxOutputTokens: number;
  /** Teto de caracteres do contexto canônico antes do truncamento determinístico. */
  contextCharBudget: number;
  /** Quantidade mínima de mensagens recentes preservadas no truncamento. */
  minRecentMessages: number;
  replyClassificationVersionId: string;
  approachTypeVersionId: string;
  publishedAt?: string;
  createdAt: string;
}

/** Versão publicada é imutável; alteração exige nova versão. */
export function isPromptVersionMutable(promptVersion: AiPromptVersion): boolean {
  return promptVersion.status === "draft";
}

// ---------------------------------------------------------------------------
// Referências tipadas de contexto
// ---------------------------------------------------------------------------

export const AI_CONTEXT_REF_TYPES = [
  "company",
  "contact",
  "lead",
  "funnel",
  "stage",
  "thread",
  "message",
  "event",
  "task",
  "prompt_version",
  "reply_classification_version",
  "approach_type_version",
] as const;
export type AiContextRefType = (typeof AI_CONTEXT_REF_TYPES)[number];

export interface AiContextRef {
  type: AiContextRefType;
  id: string;
  version?: number;
}

export function contextRefKey(ref: AiContextRef): string {
  return `${ref.type}:${ref.id}`;
}

// ---------------------------------------------------------------------------
// Pedido e resultado
// ---------------------------------------------------------------------------

/**
 * Estados explícitos. Saída inválida, parcial ou recusada NÃO vira sucesso:
 * cada uma tem estado próprio e nenhum texto é aproveitado.
 */
export const AI_REQUEST_STATUSES = [
  "pending",
  "succeeded",
  "invalid_output",
  "refused",
  "failed",
  "blocked",
] as const;
export type AiRequestStatus = (typeof AI_REQUEST_STATUSES)[number];

export const AI_TERMINAL_REQUEST_STATUSES: readonly AiRequestStatus[] = [
  "succeeded",
  "invalid_output",
  "refused",
  "failed",
  "blocked",
];

export interface AiRequest {
  id: string;
  ownerId: string;
  objective: AiObjective;
  mode: AiExecutionMode;
  leadId: string;
  companyId: string;
  contactId?: string;
  funnelId: string;
  threadId: string;
  messageId: string;
  mailboxId: string;
  contextRefs: AiContextRef[];
  /** Hash estável do contexto canônico; a cópia bruta não é persistida. */
  contextHash: string;
  contextTruncated: boolean;
  omittedRefs: AiContextRef[];
  promptVersionId: string;
  promptVersion: number;
  model: AiModel;
  reasoningEffort: AiReasoningEffort;
  maxOutputTokens: number;
  outputSchemaVersion: number;
  status: AiRequestStatus;
  errorCode?: CommandErrorCode;
  idempotencyKey: string;
  requestFingerprint: string;
  commandId: string;
  correlationId: string;
  regeneratedFromRequestId?: string;
  createdAt: string;
  completedAt?: string;
}

/** Uso normalizado do provider; nunca contém preço ou dado de credencial. */
export interface AiUsage {
  simulated: boolean;
  inputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
}

/** Compatibilidade explícita para fakes e avaliações locais. */
export interface AiSimulatedUsage extends AiUsage {
  simulated: true;
}

export interface AiFactReference {
  ref: AiContextRef;
  /** Campo canônico que sustenta o fato, já dentro do contexto montado. */
  field: string;
}

export interface AiSuggestionOutput {
  classification: ReplyClassification;
  confidence: number;
  summary: string;
  suggestedReply: string;
  recommendedApproach: string;
  factsUsed: { statement: string; reference: AiFactReference }[];
  assumptions: string[];
  missingInformation: string[];
  safetySignals: string[];
  needsHumanReview: boolean;
  handoffReason?: string;
  detectedReturnDate?: string;
}

export const AI_HANDOFF_REASONS = [
  "unknown_intent",
  "low_confidence",
  "missing_information",
  "sensitive_topic",
  "human_requested",
  "context_contradiction",
  "provider_warning",
  "provider_refusal",
  "repeated_failure",
  "suppressed_contact",
  "auto_reply_detected",
  "critical_signal",
  "out_of_office_without_date",
  "consecutive_reply_limit",
] as const;
export type AiHandoffReason = (typeof AI_HANDOFF_REASONS)[number];

export const aiHandoffReasonLabels: Record<AiHandoffReason, string> = {
  unknown_intent: "Intenção desconhecida",
  low_confidence: "Confiança abaixo do limite",
  missing_information: "Falta dado necessário",
  sensitive_topic: "Tema sensível",
  human_requested: "A pessoa pediu atendimento humano",
  context_contradiction: "Contradição entre CRM e mensagem",
  provider_warning: "Alerta do modelo",
  provider_refusal: "Recusa do modelo",
  repeated_failure: "Falha repetida",
  suppressed_contact: "Endereço suprimido",
  auto_reply_detected: "Resposta automática detectada",
  critical_signal: "Sinal crítico do canal",
  out_of_office_without_date: "Ausência sem data confiável",
  consecutive_reply_limit: "Limite de respostas consecutivas",
};

export interface AiHandoffDecision {
  required: boolean;
  reasons: AiHandoffReason[];
  /** Verdadeiro sempre que nenhuma resposta automática futura é permitida. */
  blocksAutomaticReply: boolean;
  explanation: string;
}

export interface AiResult {
  id: string;
  ownerId: string;
  requestId: string;
  outputSchemaVersion: number;
  /** Saída validada e imutável. Edição humana vive no rascunho, não aqui. */
  output: AiSuggestionOutput;
  usage: AiUsage;
  latencyMs: number;
  warnings: string[];
  refusal?: string;
  handoff: AiHandoffDecision;
  promptVersionId: string;
  promptVersion: number;
  model: AiModel;
  mode: AiExecutionMode;
  /** Resultado anterior desta cadeia de regeneração; ele permanece auditável. */
  supersedesResultId?: string;
  /** Pedido do resultado anterior; fecha a cadeia no contrato persistido. */
  supersedesRequestId?: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Feedback humano
// ---------------------------------------------------------------------------

export const AI_FEEDBACK_KINDS = ["accepted", "edited", "draft_saved", "discarded"] as const;
export type AiFeedbackKind = (typeof AI_FEEDBACK_KINDS)[number];

export const aiFeedbackKindLabels: Record<AiFeedbackKind, string> = {
  accepted: "Aceita sem edição",
  edited: "Editada por uma pessoa",
  draft_saved: "Salva como rascunho",
  discarded: "Descartada",
};

export interface AiFeedback {
  id: string;
  ownerId: string;
  requestId: string;
  resultId: string;
  threadId: string;
  mailboxId: string;
  kind: AiFeedbackKind;
  /** Hash do texto humano; o corpo vive no rascunho do canal de e-mail. */
  editedTextHash?: string;
  draftMessageId?: string;
  note?: string;
  commandId: string;
  correlationId: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Orçamento
// ---------------------------------------------------------------------------

/**
 * Preço corrente NÃO é codificado como verdade durável: a tabela de preços do
 * fornecedor e a configuração real do proprietário continuam pendentes.
 */
export const AI_PRICING_PENDING = true;

export const AI_BUDGET_WARNING_RATIO = 0.8;

export interface AiBudgetPeriod {
  id: string;
  ownerId: string;
  periodStart: string;
  periodEnd: string;
  /** Sem default: ausência de limite é decisão do proprietário, não zero. */
  limitCents: number | null;
  confirmedUsageCents: number;
  reservedUsageCents: number;
  warningNotifiedAt?: string;
  blockedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export const AI_BUDGET_STATES = ["no_budget", "ok", "warning", "exhausted"] as const;
export type AiBudgetState = (typeof AI_BUDGET_STATES)[number];

export const AI_BUDGET_BLOCK_REASONS = ["missing_budget", "budget_exhausted"] as const;
export type AiBudgetBlockReason = (typeof AI_BUDGET_BLOCK_REASONS)[number];

export interface AiBudgetDecision {
  state: AiBudgetState;
  allowed: boolean;
  blockReason?: AiBudgetBlockReason;
  usedRatio: number | null;
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Avaliações
// ---------------------------------------------------------------------------

export interface AiEvaluationCase {
  id: string;
  ownerId: string;
  name: string;
  objective: AiObjective;
  /** Intenção esperada; agrupa as métricas por classificação. */
  expectedClassification: ReplyClassification;
  expectedNeedsHumanReview: boolean;
  expectedHandoffReasons: AiHandoffReason[];
  scenarioKey: string;
  active: boolean;
  createdAt: string;
}

export interface AiEvaluationCaseOutcome {
  caseId: string;
  scenarioKey: string;
  expectedClassification: ReplyClassification;
  observedClassification?: ReplyClassification;
  classificationMatched: boolean;
  schemaValid: boolean;
  handoffMatched: boolean;
  factsGrounded: boolean;
  crossReferenceLeak: boolean;
  criticalSignalRespected: boolean;
  injectionResisted: boolean;
  notes: string[];
}

export interface AiEvaluationRun {
  id: string;
  ownerId: string;
  promptVersionId: string;
  model: AiModel;
  mode: AiExecutionMode;
  outcomes: AiEvaluationCaseOutcome[];
  /** Métricas locais do fake; NÃO declaram qualidade do modelo real. */
  metrics: {
    total: number;
    classificationAccuracy: number;
    schemaValidRate: number;
    handoffAccuracy: number;
    factsGroundedRate: number;
    crossReferenceLeaks: number;
    criticalSignalRespectRate: number;
    injectionResistanceRate: number;
    byClassification: Record<string, { total: number; matched: number }>;
  };
  createdAt: string;
}
